import React from 'react';
import ReactSelect from 'react-select';
import classNames from 'classnames';
import { palette } from '../../constants/colorsConstants';
import classes from './AppSelect.module.scss';
import { SelectKeyboardListenersNames } from '../CurrencySelect/types';

export const formatOptionLabel = (
  {
    customOptionWrapperClass,
    optionLabelClass,
    optionInnerSubLabel,
    keyboardSelectedItem,
    onClick = (selected: string) => {},
  },
  { label, description, ref, value, innerDescription },
) => {
  return (
    <div className={customOptionWrapperClass ?? classes.customOptionWrapper} ref={ref} onClick={() => onClick(value)}>
      <div
        className={classNames(
          optionLabelClass ?? classes.optionLabel,
          keyboardSelectedItem === value && classes.keyboardSelected,
        )}
      >
        <div>{label}</div>
        {innerDescription ? <div className={optionInnerSubLabel}>{innerDescription}</div> : null}
      </div>
      <div className={classes.optionSubLabel}>{description}</div>
    </div>
  );
};

export const CustomInputValue = ({ data }) => (
  <div className={classes.selectOptionWrapper}>
    <div>{data.label}</div>
  </div>
);
const { gray, blue } = palette;
export const getAppSelectStyles = ({
  height,
  disabled,
  isEmpty,
  backgroundColor = gray[200],
  outline,
  indicatorDisplay,
  controlFontSize,
  optionsStyle,
}: {
  height?: number;
  disabled?: boolean;
  isEmpty?: boolean;
  backgroundColor?: string;
  outline?: string | null;
  indicatorDisplay?: string | null;
  controlFontSize?: number | null;
  optionsStyle?: {
    padding?: string;
    backgroundColor?: string;
    controlBackgroundColor?: string;
  } | null;
}) => ({
  container: (base) => ({ ...base, height, borderRadius: 5 }),
  valueContainer: (base) => ({ ...base, height: '100%', display: 'flex', flexWrap: 'nowrap' }),
  indicatorsContainer: (base) => ({
    ...base,
    height,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    display: indicatorDisplay ?? (isEmpty ? 'unset' : 'none'),
    color: gray[400],
    stroke: 'none',
  }),
  indicatorSeparator: (base) => ({ ...base, display: 'none' }),
  clearIndicator: (base) => ({ ...base, color: gray[500], stroke: 'none' }),
  control: (base, state) => ({
    ...base,
    borderColor: `${state.isHovered ? gray[300] : 'transparent'} !important`,
    outline: outline ?? (state.isFocused ? `2px solid ${blue[450]}` : 'undefined'),
    borderRadius: 5,
    backgroundColor: disabled ? gray[200] : optionsStyle?.controlBackgroundColor ?? backgroundColor,
    color: disabled ? gray[400] : gray[500],
    fontSize: controlFontSize ?? 16,
    height,
    minHeight: 'unset',
    boxShadow: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
  placeholder: (base) => ({
    ...base,
    color: gray[400],
  }),
  option: (base, state) => {
    const keyboardSelectedBackground = state?.data?.isFocusedByKeyboard ? blue[300] : null;

    return {
      ...base,
      backgroundColor:
        optionsStyle?.backgroundColor ?? keyboardSelectedBackground ?? (state.isSelected ? blue[200] : 'white'),
      cursor: disabled ? 'not-allowed' : 'pointer',
      ...(optionsStyle ?? {}),
      width: '95%',
    };
  },
  input: (base) => ({
    ...base,
    color: gray[500],
    fontSize: controlFontSize ?? 16,
    width: '100%',
  }),
});

export interface AppSelectProps {
  className?: string;
  value: any;
  options: any[];
  placeholder?: string;
  onChange(selected: string): void;
  onClick?: (e: any) => void;
  automationid?: string;
  disabled?: boolean;
  height?: number;
  invalid?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  outline?: string;
  indicatorDisplay?: string;
  indicatorComponent?: React.ComponentType<any>;
  controlFontSize?: number;
  optionsStyle?: {
    padding?: string;
    backgroundColor?: string;
    controlBackgroundColor?: string;
  };
  customOptionWrapperClass?: string;
  optionLabelClass?: string;
  optionInnerSubLabel?: string;
  onKeyDown?: (e: { code: string } | null, newValue?: string) => void;
  menuIsOpen?: boolean;
  searchValue?: string | null;
  autoFocus?: boolean;
  keyboardSelectedItem?: string | null;
}

const AppSelect: React.FC<AppSelectProps> = ({
  className,
  value,
  options,
  onChange,
  onClick,
  disabled,
  invalid,
  outline,
  indicatorDisplay,
  indicatorComponent,
  controlFontSize,
  optionsStyle,
  customOptionWrapperClass,
  optionLabelClass,
  optionInnerSubLabel,
  onKeyDown,
  searchValue,
  autoFocus,
  placeholder = 'Select',
  height = 36,
  isClearable = true,
  isSearchable = false,
  menuIsOpen = null,
  keyboardSelectedItem = null,
  ...reactSelectProps
}) => {
  const activeOption = options?.find((o) => o?.value === value) ?? value;
  const selectComponents = {
    SingleValue: CustomInputValue,
  } as {
    SingleValue: React.ComponentType<any>;
    DropdownIndicator?: React.ComponentType<any>;
    Input?: React.ComponentType<any>;
  };
  if (indicatorComponent) {
    selectComponents.DropdownIndicator = indicatorComponent;
  }

  const handleReactSelectKeyDown = (e) => {
    const keyObj = { code: e.key };

    onKeyDown?.(keyObj, searchValue ?? '');
  };

  const handleInputChange = (newValue, e) => {
    if (e.action === 'input-change') {
      if (!newValue?.length) {
        onKeyDown?.({ code: SelectKeyboardListenersNames.BACKSPACE }, newValue);
      } else {
        onKeyDown?.(null, newValue);
      }
    }

    return newValue;
  };
  return (
    <ReactSelect
      {...reactSelectProps}
      inputValue={!activeOption ? searchValue! : undefined}
      onInputChange={handleInputChange}
      onKeyDown={handleReactSelectKeyDown}
      menuIsOpen={menuIsOpen ? menuIsOpen : undefined}
      isSearchable={isSearchable}
      className={classNames(classes.appSelect, className, invalid && classes.invalid)}
      classNamePrefix="app-select"
      isClearable={isClearable}
      value={searchValue?.length ? searchValue : activeOption}
      components={selectComponents}
      formatOptionLabel={(props) => {
        return formatOptionLabel(
          {
            customOptionWrapperClass,
            optionLabelClass,
            optionInnerSubLabel,
            keyboardSelectedItem,
            onClick: (e: any) => onClick?.(e),
          },
          props,
        );
      }}
      menuShouldScrollIntoView={false}
      autoFocus={autoFocus ?? true}
      options={options}
      placeholder={searchValue?.length ? searchValue : placeholder}
      onChange={(option, e) => onChange(option?.value)}
      isDisabled={disabled}
      styles={getAppSelectStyles({
        controlFontSize,
        optionsStyle,
        indicatorDisplay,
        height,
        disabled,
        isEmpty: !value,
        outline,
      })}
      menuPlacement="auto"
    />
  );
};

export default AppSelect;
